import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import {
  ActivityQuery,
  AuthConfig,
  createRecordFromArray,
  isAdminUser,
  ListTestResponse,
  NonArchPlatform,
  pluralize,
  queries,
  TestsActivity,
  Threat,
  ThreatsActivity,
  useAccountQuery,
  useAuthConfig,
} from "@theprelude/core";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  IconButton,
  MoreIcon,
  SearchIcon,
} from "@theprelude/ds";
import { useState } from "react";
import { z } from "zod";
import { usePopper } from "@/hooks/use-popper";
import { createQuery } from "@/lib/create-query";
import {
  ExpectedResult,
  getExpectedResult,
  getPersonalPercentages,
  OptionalODPFields,
} from "@/lib/odp";
import { getActivityQuery } from "@/lib/validators";
import { DefaultOverlay, OverlayErrorFallback } from "../default-overlay";
import { DeleteThreatModal } from "../modal/delete-threat-modal";
import {
  authorColumn,
  defaultHidden,
  expectedResultColumn,
  getColumnVisibility,
  odpColumns,
  orderColumn,
  supportedPlatformsColumn,
  techniqueColumn,
  testActionsColumn,
  testIdColumn,
  testNameSmallColumn,
  testProtectedColumn,
} from "../table/columns";
import { TableFilterInput } from "../table/filter-input";
import { useTable } from "../table/hooks";
import { VirtualTable } from "../table/virtual-table";
import EditThreat from "./edit-threat";
import ViewThreat from "./view-threat";

export type ThreatActivityEntry = OptionalODPFields & {
  order: number;
  name: string;
  testId: string;
  account_id: string | undefined;
  author: string | undefined;
  technique?: string;
  protected?: number;
  benchmark?: number;
  expectedResult?: ExpectedResult;
  supported_platforms: NonArchPlatform[];
};

export const threatOverlaySchema = z.tuple([
  z.literal("threat"),
  z.object({ id: z.string() }),
]);

type ThreatOverlaySchema = z.infer<typeof threatOverlaySchema>;

function getThreatOverlayData(
  threat: Threat,
  threatsActivity: ThreatsActivity[],
  tests: ListTestResponse[],
  testsActivity: TestsActivity[],
) {
  const testsById = createRecordFromArray(tests, "id");

  if (!threat) {
    return {
      result: null,
      error: new Error(`Threat not found`),
    };
  }

  const threatActivity = threatsActivity.find((t) => t.id === threat.id);
  const testActivity = testsActivity.filter((test) =>
    threat.tests.includes(test.id),
  );
  const relevantTests = threat.tests
    .map((id) => testsById[id])
    .filter((t): t is ListTestResponse => t !== undefined);

  const testActivityById = createRecordFromArray(testActivity, "id");

  const rows: ThreatActivityEntry[] = relevantTests.map((t, index) => {
    const usage = testActivityById[t.id];
    return {
      order: index + 1,
      name: t.name,
      testId: t.id,
      account_id: t.account_id ?? undefined,
      author: t.author ?? undefined,
      technique: t.technique ?? undefined,
      protected: usage?.protected ?? undefined,
      ...getPersonalPercentages(usage?.personal),
      expectedResult: getExpectedResult(t.expected, usage?.personal),
      benchmark: testActivityById[t.id]?.social ?? undefined,
      supported_platforms: t.supported_platforms,
    };
  });

  return {
    result: {
      threat,
      threatActivity,
      rows,
    },
    error: null,
  };
}

const threatQueryOptions = (
  authConfig: AuthConfig,
  query: ActivityQuery,
  id: string,
) =>
  createQuery({
    queries: [
      queries.detect.getThreat(authConfig, id),
      queries.detect.activity.threats(authConfig, query),
      queries.detect.tests(authConfig),
      queries.detect.activity.tests(authConfig, query),
    ],
    queryFn: getThreatOverlayData,
  });

function ThreatDropdown({
  id,
  setMode,
}: {
  id: string;
  setMode: (mode: "edit" | "view") => void;
}) {
  const deleteThreatDialog = usePopper();
  const authConfig = useAuthConfig();
  const { data: account } = useAccountQuery();
  const { data: threat } = useQuery(queries.detect.getThreat(authConfig, id));
  const isAdmin = isAdminUser(account) ?? false;

  const cannotInteract =
    !threat || threat.account_id !== account?.account_id || !isAdmin;

  return (
    <>
      <DeleteThreatModal {...deleteThreatDialog} id={id} />
      <Dropdown>
        <DropdownTrigger asChild>
          {<IconButton icon={<MoreIcon />} />}
        </DropdownTrigger>
        <DropdownContent align="end">
          <DropdownItem
            onSelect={() => setMode("edit")}
            disabled={cannotInteract}
          >
            Edit this threat
          </DropdownItem>
          <DropdownItem
            onSelect={deleteThreatDialog.on}
            disabled={cannotInteract}
          >
            Delete this threat
          </DropdownItem>
        </DropdownContent>
      </Dropdown>
    </>
  );
}

function ThreatView({
  id,
  mode,
  setMode,
}: {
  id: string;
  mode: "edit" | "view";
  setMode: (mode: "edit" | "view") => void;
}) {
  const authConfig = useAuthConfig();
  const query = getActivityQuery();

  const threatOverlayQuery = threatQueryOptions(
    authConfig,
    query,
    id,
  ).useSuspenseQuery();

  const { result, error } = threatOverlayQuery.data;

  if (error) {
    return <OverlayErrorFallback error={error} />;
  }

  const { threat, rows, threatActivity } = result;

  if (mode === "edit") {
    return <EditThreat threat={threat} setMode={setMode} query={query} />;
  }

  return (
    <ViewThreat threat={threat} threatActivity={threatActivity} rows={rows} />
  );
}

export function ThreatOverlay({ id }: ThreatOverlaySchema[1]) {
  const [mode, setMode] = useState<"edit" | "view">("view");

  return (
    <DefaultOverlay
      title="Threat"
      actionsDropdown={<ThreatDropdown id={id} setMode={setMode} />}
    >
      <ThreatView id={id} mode={mode} setMode={setMode} />
    </DefaultOverlay>
  );
}

const columnHelper = createColumnHelper<ThreatActivityEntry>();

const columns = [
  columnHelper.accessor("order", orderColumn),
  columnHelper.accessor("testId", defaultHidden(testIdColumn)),
  columnHelper.accessor("name", testNameSmallColumn),
  columnHelper.accessor("author", authorColumn),
  columnHelper.accessor("technique", techniqueColumn),
  columnHelper.accessor("supported_platforms", supportedPlatformsColumn),
  columnHelper.accessor("protected", testProtectedColumn),
  ...odpColumns(columnHelper),
  columnHelper.accessor("expectedResult", expectedResultColumn),
  columnHelper.accessor("testId", testActionsColumn),
];

const ThreatActivityTable: React.FC<{
  activity: ThreatActivityEntry[];
}> = ({ activity }) => {
  const [searchInputActive, setSearchInputActive] = useState<boolean>(false);

  const table = useTable({
    name: "threat-activity",
    data: activity,
    columns,
    initialState: { columnVisibility: getColumnVisibility(columns) },
  });

  return (
    <>
      <div className="flex items-center justify-between border-stroke-line border-b border-dashed p-4">
        {!searchInputActive && (
          <span className="flex items-center py-2.5 text-secondary-foreground text-xs">
            {activity.length} Verified Security{" "}
            {pluralize(activity.length, "Test")}
          </span>
        )}

        {searchInputActive ? (
          <TableFilterInput
            className="lg:w-full"
            table={table}
            onClose={() => setSearchInputActive(false)}
          />
        ) : (
          <IconButton
            icon={<SearchIcon />}
            onClick={() => setSearchInputActive(true)}
          />
        )}
      </div>

      <section className="p-4">
        <VirtualTable size="large" className="max-h-[500px]" table={table} />
      </section>
    </>
  );
};

export default ThreatActivityTable;
